import { BusyMark, Button } from "@minket/ui/components/button";
import { StatusDot, type StatusTone } from "@minket/ui/components/status-dot";
import { cn } from "@minket/ui/lib/utils";
import type { UseQueryResult } from "@tanstack/react-query";
import { lazy, type ReactNode, Suspense } from "react";
import { describeError } from "@/lib/api";

/**
 * The states every page shares, so a page is never a crash or a blank strip:
 *
 * - loading: the words "Loading emails." beside the still waiting mark, over the page's ghost
 *   (its real column heads or sections, unfilled), so loading never looks like empty;
 * - error: what happened in the API's own sentence, the request id to quote, and Try again.
 *   Every failure takes this path, a 501 included: the console and the API deploy together, so
 *   an endpoint the API does not serve means the two are out of step, and the API's own
 *   sentence says so ("Check the deployment's version");
 * - the data itself, handed to the page.
 */

type HeadingLevel = 2 | 3;

export interface ErrorStateProps {
  title: string;
  error: unknown;
  onRetry?: () => void;
  headingLevel?: HeadingLevel;
  className?: string;
}

export function LoadingState({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <div aria-busy="true" className="grid min-w-0 gap-3">
      <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <BusyMark className="size-3.5" />
        {label}.
      </p>
      {children}
    </div>
  );
}

/** The failure itself, which a page loads with its first failure, not with every page. */
const LoadedErrorState = lazy(() =>
  import("./error-state").then((module) => ({ default: module.ErrorState })),
);

/**
 * A failure, set as part of the page rather than boxed apart from it (error-state.tsx): a 2 px
 * rule in the alarm ink down its edge, the title, the API's sentence, what to do, the request id
 * to quote with Copy, and Try again; an alert, so it is announced as it appears. Its code loads
 * with the first failure; until then the rule and the title stand in its place.
 */
export function ErrorState(props: ErrorStateProps) {
  const Heading = (props.headingLevel ?? 2) === 2 ? "h2" : "h3";
  return (
    <Suspense
      fallback={
        <section
          data-state="error"
          role="alert"
          className={cn(
            "grid min-w-0 content-start gap-2 border-l-2 border-destructive py-1 pl-5",
            props.className,
          )}
        >
          <Heading className="type-title text-xl wrap-break-word">{props.title}</Heading>
        </section>
      }
    >
      <LoadedErrorState {...props} />
    </Suspense>
  );
}

/**
 * A one-line state inside a section: a sentence, with the lamp of its status when it has one (a
 * neutral sentence has no status word, so no lamp).
 */
export function Notice({
  tone = "neutral",
  children,
}: {
  tone?: Exclude<StatusTone, "primary">;
  children: ReactNode;
}) {
  return (
    <p className="flex min-w-0 items-start gap-2.5 text-sm text-muted-foreground">
      {tone === "neutral" ? null : <StatusDot tone={tone} className="mt-1.5" />}
      <span className="min-w-0 wrap-break-word">{children}</span>
    </p>
  );
}

/**
 * A query's states for a page body. `noun` finishes "Loading …" and "Could not load …";
 * `preview` draws the page's shape while it loads.
 */
export function QueryView<T>({
  query,
  noun,
  preview,
  headingLevel = 2,
  children,
}: {
  query: UseQueryResult<T>;
  noun: string;
  /** What the page will show, in one sentence. Kept for callers; the states no longer need it. */
  will?: string;
  icon?: ReactNode;
  preview?: (busy: boolean) => ReactNode;
  headingLevel?: HeadingLevel;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) {
    return <LoadingState label={`Loading ${noun}`}>{preview?.(true)}</LoadingState>;
  }
  if (query.isError) {
    return (
      <ErrorState
        title={`Could not load ${noun}`}
        error={query.error}
        onRetry={() => void query.refetch()}
        headingLevel={headingLevel}
      />
    );
  }
  return children(query.data);
}

/** The same states in one line, for a section (settings, the overview). */
export function QueryNotice<T>({
  query,
  noun,
  children,
}: {
  query: UseQueryResult<T>;
  noun: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) {
    return (
      <div aria-busy="true">
        <Notice>Loading {noun}.</Notice>
      </div>
    );
  }
  if (query.isError) {
    const { message, requestId } = describeError(query.error);
    return (
      <div role="alert" className="grid gap-2">
        <Notice tone="destructive">
          Could not load {noun}. {message}
          {requestId ? (
            <>
              {" "}
              Request id <code className="type-data text-foreground">{requestId}</code>.
            </>
          ) : null}
        </Notice>
        <div>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  return children(query.data);
}
